import { ApiResponse } from "@src/types/httpApi";
import {
  ICreateScheduleForm,
  IMasterScheduleEvent,
  IScheduleBoardEntry,
  IScheduleBoardState,
  IScheduleEvent,
  ISubmittedPlaylistLite,
} from "@src/types/schedules";
import httpApi from "@src/utils/httpApi";
import { AxiosResponse } from "axios";
import moment from "moment";

export const fetchApiEvents = (
  startTime: Date,
  endTime: Date
): Promise<AxiosResponse<ApiResponse<IScheduleEvent[]>>> => {
  return httpApi.get("/api/schedules", {
    params: {
      populate: {
        show: {
          fields: ["title", "description", "frequency"],
          populate: {
            curators: {
              fields: ["name"],
            },
          },
        },
        playlist: {
          fields: ["title", "playlistStatus"],
          populate: {
            curator: {
              fields: ["name"],
            },
          },
        },
      },
      "filters[startTime][$gte]": new Date(
        startTime.getTime() - 24 * 60 * 60 * 1000
      ).toISOString(),
      "filters[startTime][$lt]": endTime.toISOString(),
      "pagination[page]": 1,
      "pagination[pageSize]": 500,
    },
  });
};

export const fetchApiMasterEvents = async (): Promise<
  AxiosResponse<ApiResponse<IMasterScheduleEvent[]>>
> => {
  return httpApi.get<ApiResponse<IMasterScheduleEvent[]>>(
    "/api/master-schedule"
  );
};

// Shared, admin-only Schedule Board draft (single-type). GET returns the stored
// arrangement (or an empty/null state when never saved); PUT overwrites it.
// Draft-only — this never touches the live `schedule` collection.
export const fetchScheduleBoard = (): Promise<
  AxiosResponse<ApiResponse<IScheduleBoardEntry>>
> => {
  return httpApi.get<ApiResponse<IScheduleBoardEntry>>("/api/schedule-board");
};

export const saveScheduleBoard = (
  state: IScheduleBoardState
): Promise<AxiosResponse<ApiResponse<IScheduleBoardEntry>>> => {
  return httpApi.put<ApiResponse<IScheduleBoardEntry>>("/api/schedule-board", {
    data: { state },
  });
};

// Each show documentId adds its own `filters[show][documentId][$in][N]` query
// param, so a calendar with many shows produces a URL long enough to trip the
// server's URI length limit (HTTP 414, surfaced in the browser as a CORS
// error). Requesting the shows in small batches keeps every URL well under that
// limit no matter how many shows are on the calendar.
const SUBMITTED_PLAYLISTS_SHOW_BATCH_SIZE = 20;

const chunkArray = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

// Fetch playlists with status "submitted" for the given shows. Used to show how
// many playlists have been submitted for a slot that has no playlist attached.
export const fetchSubmittedPlaylistsByShows = async (
  showDocumentIds: string[]
): Promise<AxiosResponse<ApiResponse<ISubmittedPlaylistLite[]>>> => {
  const buildParams = (ids: string[]): Record<string, string | number> => {
    const params: Record<string, string | number> = {
      "filters[playlistStatus][$eq]": "submitted",
      "fields[0]": "id",
      "fields[1]": "documentId",
      "populate[show][fields][0]": "documentId",
      "pagination[page]": 1,
      "pagination[pageSize]": 500,
    };
    ids.forEach((docId, index) => {
      params[`filters[show][documentId][$in][${index}]`] = docId;
    });
    return params;
  };

  const batches = showDocumentIds.length
    ? chunkArray(showDocumentIds, SUBMITTED_PLAYLISTS_SHOW_BATCH_SIZE)
    : [[]];
  const responses = await Promise.all(
    batches.map((ids) =>
      httpApi.get<ApiResponse<ISubmittedPlaylistLite[]>>("/api/playlists", {
        params: buildParams(ids),
      })
    )
  );

  const mergedData = responses.flatMap(
    (response: AxiosResponse<ApiResponse<ISubmittedPlaylistLite[]>>) =>
      response.data?.data || []
  );
  const base = responses[0];
  return {
    ...base,
    data: { ...base.data, data: mergedData },
  };
};

// Locate the concrete schedule event backing a master-schedule slot. The
// /api/master-schedule endpoint returns the next airing time but not the
// underlying schedule event's documentId, which the comparison-view "Apply"
// action needs to replace the slot's show. Fetch the show's events within a
// one-day window around the slot so the caller can pick the closest match
// (tolerating minor timezone/format differences between the two endpoints).
export const fetchSchedulesByShowAround = (
  showDocumentId: string,
  aroundIso: string
): Promise<AxiosResponse<ApiResponse<IScheduleEvent[]>>> => {
  const around = moment(aroundIso);
  return httpApi.get<ApiResponse<IScheduleEvent[]>>("/api/schedules", {
    params: {
      "filters[show][documentId][$eq]": showDocumentId,
      "filters[startTime][$gte]": around
        .clone()
        .subtract(1, "day")
        .toISOString(),
      "filters[startTime][$lt]": around.clone().add(1, "day").toISOString(),
      "fields[0]": "documentId",
      "fields[1]": "startTime",
      "pagination[page]": 1,
      "pagination[pageSize]": 50,
    },
  });
};

export const createScheduleEventApi = (
  data: ICreateScheduleForm
): Promise<AxiosResponse<ApiResponse<IScheduleEvent>>> => {
  const scheduleEventBody: {
    startTime: string;
    endTime: string;
    playlist?: {
      connect: { documentId: string }[];
    };
    show?: {
      connect: { documentId: string }[];
    };
  } = {
    startTime: moment(data.startTime).format("YYYY-MM-DD HH:mm:ss"),
    endTime: moment(data.endTime).format("YYYY-MM-DD HH:mm:ss"),
  };
  if (data.playlistId) {
    scheduleEventBody.playlist = {
      connect: [{ documentId: data.playlistId }],
    };
  }
  if (data.showId) {
    scheduleEventBody.show = {
      connect: [{ documentId: data.showId }],
    };
  }
  return httpApi.post("/api/schedules", {
    data: scheduleEventBody,
  });
};
export const updateScheduleEventApi = (
  data: ICreateScheduleForm & { scheduleId: string }
): Promise<AxiosResponse<ApiResponse<IScheduleEvent>>> => {
  const requestData: any = {
    startTime: moment(data.startTime).format("YYYY-MM-DD HH:mm:ss"),
    endTime: moment(data.endTime).format("YYYY-MM-DD HH:mm:ss"),
  };

  if (data.playlistId) {
    requestData.playlist = {
      set: [{ documentId: data.playlistId }],
    };
  } else {
    requestData.playlist = {
      set: [],
    };
  }

  if (data.showId) {
    requestData.show = {
      set: [{ documentId: data.showId }],
    };
  } else {
    requestData.show = {
      set: [],
    };
  }

  return httpApi.put(`/api/schedules/${data.scheduleId}`, {
    data: requestData,
  });
};
